import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { COPY, RUNTIME } from "../lib/_data";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const inputCls =
  "bg-[#fffdf9] border-[#e5e0d8] focus-visible:ring-2 focus-visible:ring-[#c85a3c] focus-visible:ring-offset-0 focus-visible:border-[#c85a3c] rounded-lg h-12 text-[#2c2a29] placeholder:text-[#a8a29d]";

export default function ContactForm() {
  // Iter-77 — all visible form copy is editable via copy_overrides
  // (`forms.contact.*`). Submission behavior is unchanged.
  const fc = (COPY.forms && COPY.forms.contact) || {};
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      if (RUNTIME.slug) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/public/site/${RUNTIME.slug}/contact`,
          { name: form.name, email: form.email, phone: form.phone, message: form.message, source: "contact" }
        );
      }
      toast.success(fc.successTitle || "Message received!", {
        description: fc.successBody || "We'll get back to you within the hour.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(fc.errorTitle || "Something went wrong.", {
        description: fc.errorBody || "Please try again, or call us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      noValidate
      className="bg-[#fffdf9] rounded-3xl border border-[#e5e0d8] p-6 md:p-10 shadow-[0_20px_60px_rgba(44,42,41,0.08)]"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.nameLabel || "Full Name *"}
          </Label>
          <Input
            data-testid="contact-name"
            className={`mt-2 ${inputCls}`}
            placeholder={fc.namePlaceholder || "Jane Doe"}
            value={form.name}
            onChange={set("name")}
            required
          />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.phoneLabel || "Phone"}
          </Label>
          <Input
            data-testid="contact-phone"
            type="tel"
            className={`mt-2 ${inputCls}`}
            placeholder={fc.phonePlaceholder || "(555) 000-1234"}
            value={form.phone}
            onChange={set("phone")}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.emailLabel || "Email *"}
          </Label>
          <Input
            data-testid="contact-email"
            type="email"
            className={`mt-2 ${inputCls}`}
            placeholder={fc.emailPlaceholder || "jane@email.com"}
            value={form.email}
            onChange={set("email")}
            required
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-[#5c5855]">
            {fc.messageLabel || "How can we help? *"}
          </Label>
          <Textarea
            data-testid="contact-message"
            className={`mt-2 min-h-[140px] ${inputCls}`}
            placeholder={fc.messagePlaceholder || "Tell us what you need — a repair, a quote, a question…"}
            value={form.message}
            onChange={set("message")}
            required
          />
        </div>
      </div>
      <button
        type="submit"
        data-testid="contact-submit"
        disabled={submitting}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#c85a3c] hover:bg-[#a64b32] disabled:opacity-70 text-[#fffdf9] font-bold px-8 py-4 rounded-full text-base transition-all shadow-[0_10px_28px_rgba(200,90,60,0.35)]"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> {fc.sendingLabel || "Sending…"}
          </>
        ) : (
          <>
            {fc.submitLabel || "Send Message"} <Send className="w-4 h-4" />
          </>
        )}
      </button>
    </form>
  );
}
